'use client';
import { Input } from '@ui/components/moleculas';
import { IMessagePayload } from '@ui/components';
import { useFiles, usePostHogFeatureFlag } from '@ui/hooks';
import { COMMUNITY_MAX_FILES_CREATOR, COMMUNITY_MAX_FILES_FAN } from '@ui/hooks/file/constants';
import { useCreatorVideoUploadPermission } from '@ui/hooks/use-creator-video-upload-permission';
import dynamic from 'next/dynamic';
import { useEffect, useState, useRef } from 'react';
import { X } from 'lucide-react';
import { CreatingContentBanner } from './common/creating-content-banner';
import type { IPollDraftServerError } from './common/message-poll-draft-editor';
import { toMessagePollInput, type IMessagePollDraft } from '@polls/message-poll';

const FAN_MAX_FILES_MESSAGE =
  'You can upload up to 4 files here. Upload up to 10 files in the mobile app.';

const RichTextEditor = dynamic(
  () => import('@ui/components/ui/rte-editor/rte-editor').then((mod) => mod.RichTextEditor),
  { ssr: false },
);

const initialState: IMessagePayload = {
  send_email: false,
  files: {
    images: [],
  },
  name: '',
  description: '',
  post: true,
  type: 'post',
};

interface BasePostCreationFormProps {
  initialValues?: Partial<IMessagePayload>;
  onSubmit: (
    message: IMessagePayload,
    description: string,
    mentions: string[],
    clearEditorContent: () => void,
    clearMessage: () => void,
    //   discardFiles: () => void,
    discardFiles: (opts?: { revoke?: boolean }) => void,
    showPollInvalid?: (error: IPollDraftServerError) => void,
  ) => Promise<void>;
  isSubmitDisabled?: boolean;
  isLoading?: boolean;
  authorAvatarURL?: string;
  userType?: 'creator' | 'fan';
  currentUser?: 'creator' | 'fan';
  placeholder?: string;
  editorLabel?: string;
  buttonLabel?: string;
  showCancelButton?: boolean;
  enableSuggestions?: boolean;
  initialMention?: { id: string; label: string };
  enableSendEmailToggle?: boolean;
  showSaveDraftButton?: boolean;
  authorAvatarFallbackName?: string;
  onCancel?: () => void;
  creatorData?: { platform_settings?: { enable_video_uploads?: boolean } };
}

export const BasePostCreationForm = ({
  initialValues = {
    ...initialState,
  },
  onSubmit,

  isSubmitDisabled = false,
  isLoading = false,
  authorAvatarURL,
  userType = 'creator',
  currentUser = 'creator',
  placeholder = 'Compose content...',
  editorLabel = 'Create content',
  buttonLabel = 'Submit',
  showCancelButton = false,
  enableSuggestions = false,
  initialMention,
  enableSendEmailToggle = false,
  showSaveDraftButton = false,
  authorAvatarFallbackName,
  onCancel,
  creatorData,
}: BasePostCreationFormProps) => {
  const [message, setMessage] = useState<IMessagePayload>({
    send_email: false,
    files: { images: [] },
    name: '',
    description: '',
    post: true,
    type: 'post',
    ...initialValues,
  });
  const isVideoStreamUploadEnabled = usePostHogFeatureFlag('video-stream-upload');
  const { isVideoUploadEnabled } = useCreatorVideoUploadPermission(creatorData);
  const maxFiles = userType === 'creator' ? COMMUNITY_MAX_FILES_CREATOR : COMMUNITY_MAX_FILES_FAN;
  const { files, onSelectFiles, deleteFile, onDiscardFiles, error } = useFiles({
    maxFiles,
    allowVideos: isVideoStreamUploadEnabled && isVideoUploadEnabled ? true : false,
    maxFilesErrorCreatorStyle: userType === 'creator',
  });
  const [pollDraft, setPollDraft] = useState<IMessagePollDraft | null>(null);
  const [pollServerError, setPollServerError] = useState<IPollDraftServerError | null>(null);
  const [displayError, setDisplayError] = useState<string | null>(null);
  const [errorDismissed, setErrorDismissed] = useState(false);
  const errorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isFanMaxFilesError =
    userType === 'fan' && error != null && error.includes('only add up to 4');

  useEffect(() => {
    setMessage((state) => ({ ...state, files: { images: files } }));
  }, [files]);

  useEffect(() => {
    if (error) {
      setErrorDismissed(false);
      setDisplayError(isFanMaxFilesError ? FAN_MAX_FILES_MESSAGE : error);
      if (errorTimerRef.current) {
        clearTimeout(errorTimerRef.current);
      }
      // Don't auto-dismiss fan max-files error; it stays until user closes, reselects, or posts
      if (!isFanMaxFilesError) {
        errorTimerRef.current = setTimeout(() => {
          setDisplayError(null);
        }, 10000);
      }
    } else {
      setDisplayError(null);
    }

    return () => {
      if (errorTimerRef.current) {
        clearTimeout(errorTimerRef.current);
      }
    };
  }, [error, isFanMaxFilesError]);

  const onCancelEditor = () => {
    setMessage(initialState);
    setPollDraft(null);
    setPollServerError(null);
    onDiscardFiles();
    setDisplayError(null);
    setErrorDismissed(false);
    onCancel?.();
  };

  const handleSendEmailToggle = (value: boolean) => {
    setMessage((prev) => ({ ...prev, send_email: value }));
  };

  const handlePollDraftChange = (draft: IMessagePollDraft | null) => {
    setPollServerError(null);
    setPollDraft(draft);
  };

  const handleSubmit = (
    description: string,
    mentions: string[],
    isSaveDraft: boolean,
    clearEditor: () => void,
  ) => {
    setPollServerError(null);
    return onSubmit(
      {
        ...message,
        draft: isSaveDraft,
        ...(pollDraft ? { poll: toMessagePollInput(pollDraft) } : {}),
      },
      description,
      mentions,
      clearEditor,
      () => {
        setMessage(initialState);
        setPollDraft(null);
        setPollServerError(null);
      },
      onDiscardFiles,
      setPollServerError,
    );
  };

  return (
    <div className="flex flex-col gap-1">
      <CreatingContentBanner
        isVisible={userType === 'creator' && isLoading && files.length > 0}
        kind="post"
      />
      {displayError && !errorDismissed && (
        <div className="px-4 py-2 bg-red-50 border border-red-200 rounded-md flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <p className="text-sm text-red-600 font-medium">
              {isFanMaxFilesError ? FAN_MAX_FILES_MESSAGE : displayError}
            </p>
            {userType === 'fan' && !isFanMaxFilesError && (
              <p className="mt-1 text-xs text-red-600">
                You can upload up to 4 files here. Upload up to 10 files in the mobile app.
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={() => setErrorDismissed(true)}
            className="shrink-0 rounded p-1 text-red-600 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500"
            aria-label="Dismiss">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
      <Input
        value={message.name}
        type="text"
        isHideError={true}
        placeholder="Title"
        onChange={(event: any) => setMessage({ ...message, name: event.target.value })}
      />
      <RichTextEditor
        deleteFile={deleteFile}
        onSelectFiles={onSelectFiles}
        isSubmitButtonDisabled={isSubmitDisabled || !Boolean(message.name)}
        authorAvatarURL={authorAvatarURL}
        onChange={handleSubmit}
        value={message.description || ''}
        userType={userType}
        currentUser={currentUser}
        files={files}
        isLoading={isLoading}
        exceededFileLimit={files.length >= maxFiles}
        placeholder={placeholder}
        editorLabel={editorLabel}
        buttonLabel={buttonLabel}
        maxFiles={maxFiles}
        showCancelButton={showCancelButton}
        onCancel={onCancelEditor}
        enableSuggestions={enableSuggestions}
        initialMention={initialMention}
        sendEmail={message.send_email}
        onToggleSendEmail={enableSendEmailToggle ? handleSendEmailToggle : undefined}
        editorClassName="min-h-[80px]"
        showSaveDraftButton={showSaveDraftButton}
        authorAvatarFallbackName={authorAvatarFallbackName}
        creatorDisplayName={undefined}
        allowVideos={isVideoStreamUploadEnabled && isVideoUploadEnabled ? true : false}
        enablePoll
        pollDraft={pollDraft}
        onPollDraftChange={handlePollDraftChange}
        pollServerError={pollServerError}
      />
    </div>
  );
};
